import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { pushSupported, isStandalone, isPushActive, enablePush } from "@/lib/push";

// One-tap nudge to turn on phone push. Shows only when this device could receive
// push but hasn't subscribed yet — so it disappears the moment they enable it,
// and never shows on devices that already have it. On an iPhone that hasn't
// installed the PWA, it explains the Home-Screen step (iOS blocks push in Safari
// tabs). Dismiss is per-session, so it keeps nudging on later logins until on.
export function EnablePushBanner() {
  const { employee } = useAuth();
  const [active, setActive] = useState(false);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("hq_push_nudge_dismissed") === "1");

  useEffect(() => { void isPushActive().then(a => { setActive(a); setChecked(true); }); }, [employee?.id]);

  if (!employee || dismissed || !checked || active) return null;

  const iOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const dismiss = () => { sessionStorage.setItem("hq_push_nudge_dismissed", "1"); setDismissed(true); };

  // Push not available here. On iOS that's almost always "not installed yet".
  if (!pushSupported()) {
    if (!(iOS && !isStandalone())) return null; // desktop/unsupported — nothing to do
    return (
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-teal/40 bg-teal-mint/30 px-4 py-3">
        <div className="flex-1 text-sm text-gray2">
          <span className="font-semibold text-teal-dark">📲 Get alerts on your phone</span> — add HQ to your Home Screen (tap <b>Share</b> → <b>Add to Home Screen</b>), then open HQ from the icon and turn on notifications.
        </div>
        <button onClick={dismiss} className="text-sm text-gray2 hover:text-charcoal">Got it</button>
      </div>
    );
  }

  async function enable() {
    if (!employee) return;
    setBusy(true); setMsg(null);
    const r = await enablePush(employee.id);
    setBusy(false);
    if (r.ok) { setActive(true); return; }
    if (r.reason === "denied") setMsg("Notifications are blocked for HQ. Turn them on in your device settings, then try again.");
    else if (r.reason === "unsupported") setMsg("On iPhone, add HQ to your Home Screen first, then open it from the icon.");
    else setMsg(r.message || "Couldn't enable notifications.");
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-teal/40 bg-teal-mint/30 px-4 py-3">
      <div className="flex-1 text-sm">
        <span className="font-semibold text-teal-dark">🔔 Turn on phone notifications</span>
        <span className="text-gray2"> — get kudos, orders, and alerts pushed to this device even when HQ is closed.</span>
        {msg && <div className="mt-1 text-xs text-warn">{msg}</div>}
      </div>
      <button onClick={() => void enable()} disabled={busy} className="btn btn-sm">{busy ? "Enabling…" : "Turn on"}</button>
      <button onClick={dismiss} className="text-sm text-gray2 hover:text-charcoal">Not now</button>
    </div>
  );
}
